import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Users, Mail, Send, X, RotateCw, Pencil, UserX, Clock, Sparkles, Info } from "lucide-react";
import JeaCard from "@/components/jea/JeaCard";
import JeaButton from "@/components/jea/JeaButton";
import JeaInput from "@/components/jea/JeaInput";
import JeaSelect from "@/components/jea/JeaSelect";
import JeaBadge from "@/components/jea/JeaBadge";
import Skeleton from "@/components/jea/Skeleton";
import EmptyState from "@/components/jea/EmptyState";
import { apiClient, formatApiError } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { usePreviewGate } from "@/hooks/usePreviewGate";
import { relativeTime } from "@/lib/dates";
import { cn } from "@/lib/utils";

const ROLES = [
  { value: "admin",     label: "Admin",     desc: "Full access. Can manage judgments, run queries, and invite other teammates." },
  { value: "attorney",  label: "Attorney",  desc: "Read + report access. Can view all judgment data and generate reports. Cannot manage billing or team." },
  { value: "collector", label: "Collector", desc: "Query access. Can add judgments and run queries. Cannot manage billing or settings." },
  { value: "read_only", label: "Read Only", desc: "View-only access. Can browse judgments, reports, and dashboards. Cannot make changes." },
];

const roleLabel = (r) => ROLES.find((x) => x.value === r)?.label || r || "—";

const RoleBadge = ({ role }) => {
  if (role === "owner") return <JeaBadge variant="pink-tier">OWNER</JeaBadge>;
  const map = {
    admin: "warning",
    attorney: "pink",
    collector: "silver",
    read_only: "neutral",
  };
  return <JeaBadge variant={map[role] || "neutral"}>{(roleLabel(role) || "—").toUpperCase()}</JeaBadge>;
};

const StatusPill = ({ status }) => {
  if (status === "active") return <JeaBadge variant="success">Active</JeaBadge>;
  if (status === "removed") return <JeaBadge variant="danger">Removed</JeaBadge>;
  return <JeaBadge variant="neutral">{status}</JeaBadge>;
};


// ─── Invite modal ───────────────────────────────────────────────────────────
const InviteModal = ({ open, onClose, onCreated }) => {
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("attorney");
  const [busy, setBusy] = useState(false);
  const roleDesc = useMemo(() => ROLES.find((r) => r.value === role)?.desc, [role]);

  const submit = async () => {
    if (!email.trim()) { toast.error("Email required."); return; }
    setBusy(true);
    try {
      await apiClient.post("/team/invites", { email: email.trim().toLowerCase(), role });
      toast.success(`Invite sent to ${email}.`);
      onCreated?.();
      setEmail("");
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-[12px] flex items-center justify-center p-4"
         onClick={busy ? undefined : onClose}
         data-testid="invite-modal">
      <div className="w-full max-w-[480px] bg-bg-secondary border border-border-default rounded-[16px] p-8"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-2">
          <h2 className="text-white font-bold text-[20px]">Invite a Team Member</h2>
          <button type="button" onClick={onClose} className="p-1 text-text-secondary hover:text-white" data-testid="invite-modal-close">
            <X size={16} />
          </button>
        </div>
        <p className="text-text-secondary text-[14px] mb-5">
          Send an email invite. The recipient sets their own password on accepting.
        </p>
        <div className="space-y-4">
          <div>
            <div className="jea-caption mb-1.5">Email</div>
            <JeaInput
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="teammate@yourfirm.com"
              data-testid="invite-modal-email"
            />
          </div>
          <div>
            <div className="jea-caption mb-1.5">Role</div>
            <JeaSelect
              value={role}
              onChange={setRole}
              options={ROLES.map((r) => ({ value: r.value, label: r.label, description: r.desc }))}
              data-testid="invite-modal-role"
            />
          </div>
          <div className="rounded-[8px] bg-bg-tertiary p-3 flex items-start gap-2" data-testid="invite-modal-role-desc">
            <Info size={14} className="text-accent-pink shrink-0 mt-0.5" />
            <div className="text-text-secondary text-[13px]">{roleDesc}</div>
          </div>
        </div>
        <div className="mt-6 flex items-center justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-11 px-5 rounded-[8px] border border-border-default text-text-secondary hover:text-white text-[14px] font-semibold"
                  data-testid="invite-modal-cancel">Cancel</button>
          <JeaButton onClick={submit} loading={busy} className="gap-2" data-testid="invite-modal-send">
            <Send size={14} /> Send Invite
          </JeaButton>
        </div>
      </div>
    </div>
  );
};


// ─── Change role modal ──────────────────────────────────────────────────────
const RoleModal = ({ open, member, onClose, onSaved }) => {
  const [role, setRole] = useState(member?.role || "attorney");
  const [busy, setBusy] = useState(false);
  useEffect(() => { setRole(member?.role || "attorney"); }, [member]);

  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.patch(`/team/members/${member.id}`, { role });
      toast.success("Role updated.");
      onSaved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };

  if (!open || !member) return null;
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-[12px] flex items-center justify-center p-4"
         onClick={busy ? undefined : onClose}
         data-testid="role-modal">
      <div className="w-full max-w-[440px] bg-bg-secondary border border-border-default rounded-[16px] p-6"
           onClick={(e) => e.stopPropagation()}>
        <h2 className="text-white font-bold text-[18px]">Change Role — {member.full_name}</h2>
        <p className="text-text-secondary text-[13px] mt-1">{member.email}</p>
        <div className="mt-4">
          <div className="jea-caption mb-1.5">New Role</div>
          <JeaSelect
            value={role}
            onChange={setRole}
            options={ROLES.map((r) => ({ value: r.value, label: r.label }))}
            data-testid="role-modal-select"
          />
        </div>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white text-[13px] font-semibold">Cancel</button>
          <JeaButton size="sm" onClick={submit} loading={busy} data-testid="role-modal-save">Save</JeaButton>
        </div>
      </div>
    </div>
  );
};


// ─── Remove member confirmation ─────────────────────────────────────────────
const RemoveModal = ({ open, member, onClose, onRemoved }) => {
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    setBusy(true);
    try {
      await apiClient.delete(`/team/members/${member.id}`);
      toast.success(`${member.full_name} removed from the team.`);
      onRemoved?.();
      onClose?.();
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setBusy(false); }
  };
  if (!open || !member) return null;
  return (
    <div className="fixed inset-0 z-[95] bg-black/60 backdrop-blur-[12px] flex items-center justify-center p-4"
         onClick={busy ? undefined : onClose}
         data-testid="remove-modal">
      <div className="w-full max-w-[440px] bg-bg-secondary border border-danger/40 rounded-[16px] p-6"
           onClick={(e) => e.stopPropagation()}>
        <h2 className="text-danger font-bold text-[18px]">Remove {member.full_name}?</h2>
        <p className="text-text-secondary text-[13px] mt-2">
          This will invalidate all of {member.full_name}'s active sessions and revoke their access to your data.
        </p>
        <div className="mt-5 flex justify-end gap-2">
          <button type="button" onClick={onClose}
                  className="h-10 px-4 rounded-[8px] border border-border-default text-text-secondary hover:text-white text-[13px] font-semibold">Cancel</button>
          <JeaButton size="sm" variant="danger" onClick={submit} loading={busy} data-testid="remove-modal-confirm">
            Remove Member
          </JeaButton>
        </div>
      </div>
    </div>
  );
};


// ─── Team panel ─────────────────────────────────────────────────────────────
const TeamPanel = () => {
  const { user } = useAuth();
  const { guardAction } = usePreviewGate();
  const [state, setState] = useState({ owner: null, members: [], invites: [], loading: true });
  const [inviteOpen, setInviteOpen] = useState(false);
  const [roleOpen, setRoleOpen] = useState({ open: false, member: null });
  const [removeOpen, setRemoveOpen] = useState({ open: false, member: null });

  const canManage = useMemo(() => {
    if (!user) return false;
    // Owner: no parent + not admin (platform admin), or PRO owner
    if (!user.parent_account_id) return user.account_type === "pro";
    // Team admin
    return user.role === "admin";
  }, [user]);

  const load = async () => {
    setState((s) => ({ ...s, loading: true }));
    try {
      const [m, i] = await Promise.all([
        apiClient.get("/team/members"),
        apiClient.get("/team/invites"),
      ]);
      setState({
        owner: m.data.owner,
        members: m.data.members || [],
        invites: (i.data.invites || []).filter((x) => x.status === "pending"),
        loading: false,
      });
    } catch (e) {
      toast.error(formatApiError(e));
      setState((s) => ({ ...s, loading: false }));
    }
  };

  useEffect(() => { if (canManage) load(); }, [canManage]);

  if (user?.account_type !== "pro") {
    return (
      <JeaCard data-testid="team-pro-only">
        <EmptyState
          icon={Users}
          title="Team management is a PRO feature"
          description="Upgrade to PRO to invite teammates and delegate work with role-based access."
          testid="team-pro-only-empty"
        />
      </JeaCard>
    );
  }

  if (!canManage) {
    return (
      <JeaCard data-testid="team-no-access">
        <EmptyState
          icon={Users}
          title="You don't have access to team settings"
          description="Only the account owner and admins can manage team members."
          testid="team-no-access-empty"
        />
      </JeaCard>
    );
  }

  const openInvite = guardAction(() => setInviteOpen(true), { feature: "inviting team members" });

  const teamCount = 1 + state.members.filter((m) => m.status === "active").length;
  const pendingCount = state.invites.length;

  return (
    <div className="space-y-6" data-testid="team-panel">
      <div>
        <h2 className="text-white font-bold text-[18px]">Team Management</h2>
        <p className="text-text-secondary text-[14px] mt-1">
          Invite teammates and assign roles. Team members act on your data within their assigned permissions.
        </p>
      </div>

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div className="grid grid-cols-3 gap-3">
          <StatTile label="Team Members" value={teamCount} />
          <StatTile label="Pending Invites" value={pendingCount} accent={pendingCount > 0} />
          <StatTile label="Active Roles" value={new Set(state.members.filter((m) => m.status === "active").map((m) => m.role)).size} />
        </div>
        <JeaButton onClick={openInvite} className="gap-2" data-testid="team-invite-open">
          <Sparkles size={14} /> Invite Team Member
        </JeaButton>
      </div>

      {/* Members table */}
      <JeaCard data-testid="team-members-card">
        {state.loading ? (
          <Skeleton className="h-32" />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full" data-testid="team-members-table">
              <thead>
                <tr className="border-b border-border-default">
                  {["Name", "Email", "Role", "Status", "Joined", "Actions"].map((h) => (
                    <th key={h} className="jea-caption text-text-secondary p-3 text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {/* Owner row */}
                {state.owner && (
                  <tr className="border-b border-border-default" data-testid="team-row-owner">
                    <td className="p-3 text-white font-semibold">{state.owner.full_name}</td>
                    <td className="p-3 text-text-secondary text-[13px]">{state.owner.email}</td>
                    <td className="p-3"><RoleBadge role="owner" /></td>
                    <td className="p-3"><StatusPill status="active" /></td>
                    <td className="p-3 jea-date text-text-secondary text-[12px]">
                      {state.owner.created_at ? new Date(state.owner.created_at).toLocaleDateString() : "—"}
                    </td>
                    <td className="p-3 text-text-muted text-[12px]">—</td>
                  </tr>
                )}
                {state.members.map((m) => {
                  const isSelf = user?.id === m.id;
                  return (
                    <tr key={m.id} className="border-b border-border-default" data-testid={`team-row-${m.id}`}>
                      <td className="p-3 text-white font-semibold">{m.full_name}</td>
                      <td className="p-3 text-text-secondary text-[13px]">{m.email}</td>
                      <td className="p-3"><RoleBadge role={m.role} /></td>
                      <td className="p-3"><StatusPill status={m.status} /></td>
                      <td className="p-3 jea-date text-text-secondary text-[12px]">
                        {m.created_at ? new Date(m.created_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3">
                        <div className="flex items-center gap-2">
                          <button
                            type="button"
                            onClick={() => setRoleOpen({ open: true, member: m })}
                            disabled={isSelf || m.status !== "active"}
                            className={cn(
                              "p-1.5 rounded",
                              (isSelf || m.status !== "active")
                                ? "text-text-muted cursor-not-allowed"
                                : "text-text-secondary hover:text-accent-pink hover:bg-bg-tertiary"
                            )}
                            title="Change role"
                            data-testid={`team-edit-${m.id}`}
                          >
                            <Pencil size={14} />
                          </button>
                          <button
                            type="button"
                            onClick={() => setRemoveOpen({ open: true, member: m })}
                            disabled={isSelf || m.status !== "active"}
                            className={cn(
                              "p-1.5 rounded",
                              (isSelf || m.status !== "active")
                                ? "text-text-muted cursor-not-allowed"
                                : "text-text-secondary hover:text-danger hover:bg-bg-tertiary"
                            )}
                            title="Remove"
                            data-testid={`team-remove-${m.id}`}
                          >
                            <UserX size={14} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </JeaCard>

      {/* Pending invites */}
      <JeaCard data-testid="team-invites-card">
        <h3 className="text-white font-bold text-[15px] mb-3">Pending Invites</h3>
        {state.invites.length === 0 ? (
          <div className="text-text-muted text-[13px]" data-testid="team-invites-empty">No pending invites.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border-default">
                  {["Email", "Role", "Sent", "Expires", ""].map((h) => (
                    <th key={h} className="jea-caption text-text-secondary p-3 text-left">{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {state.invites.map((i) => {
                  const expiresSoon = i.expires_at && (new Date(i.expires_at).getTime() - Date.now()) < 24 * 3600 * 1000;
                  return (
                    <tr key={i.id} className="border-b border-border-default" data-testid={`invite-row-${i.id}`}>
                      <td className="p-3 text-white">{i.email}</td>
                      <td className="p-3"><RoleBadge role={i.role} /></td>
                      <td className="p-3 jea-date text-text-secondary text-[12px]">
                        {i.created_at ? relativeTime(i.created_at) : "—"}
                      </td>
                      <td className={cn("p-3 jea-mono text-[12px]", expiresSoon ? "text-warning" : "text-text-secondary")}>
                        {i.expires_at ? new Date(i.expires_at).toLocaleDateString() : "—"}
                      </td>
                      <td className="p-3">
                        <button
                          type="button"
                          onClick={async () => {
                            try {
                              await apiClient.delete(`/team/invites/${i.id}`);
                              toast.success("Invite revoked.");
                              load();
                            } catch (e) { toast.error(formatApiError(e)); }
                          }}
                          className="p-1.5 rounded text-text-secondary hover:text-danger hover:bg-bg-tertiary"
                          title="Revoke"
                          data-testid={`invite-revoke-${i.id}`}
                        >
                          <X size={14} />
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </JeaCard>

      <InviteModal open={inviteOpen} onClose={() => setInviteOpen(false)} onCreated={load} />
      <RoleModal open={roleOpen.open} member={roleOpen.member} onClose={() => setRoleOpen({ open: false, member: null })} onSaved={load} />
      <RemoveModal open={removeOpen.open} member={removeOpen.member} onClose={() => setRemoveOpen({ open: false, member: null })} onRemoved={load} />
    </div>
  );
};


const StatTile = ({ label, value, accent }) => (
  <JeaCard className="!p-4 min-w-[140px]">
    <div className="jea-caption">{label}</div>
    <div className={cn(
      "jea-mono font-extrabold text-[22px] mt-1",
      accent ? "text-warning" : "text-white"
    )}>{value}</div>
  </JeaCard>
);


export default TeamPanel;
